// Helpers for task media items, which since v0.8.6 may be a recorded video
// rather than a photo.
//
// A video is a category item like any photo — same `images` array, same
// sequence, category and per-item form data. What differs is the file ladder:
// a photo carries thumbnail/medium/full renditions of one picture, a video
// carries exactly one media file plus a poster frame. The poster lives in the
// same thumbnail fields, so any grid that only ever renders a thumbnail keeps
// working on a video item without changes.

export const MEDIA_TYPE_VIDEO = 'video';
export const MEDIA_TYPE_PHOTO = 'photo';

/**
 * Whether this item is a recorded video.
 *
 * `media_type` is the signal of record: mobile writes it from the moment of
 * capture, so it is the only one present on a clip whose upload hasn't
 * finished — a "has videoUrl" test alone would render that row as a still
 * photo and then change element type underneath the viewer when the URL
 * arrived.
 *
 * `videoUrl` / `durationMs` are recovery signals, not the primary test. A row
 * that reached the web without `media_type` — an older client, a write path
 * that predates it, a doc last touched before v0.8.6 — still carries them, and
 * rendering that clip as a photo is exactly the failure this module exists to
 * prevent. No photo row has either field, so they cannot produce a false
 * positive.
 */
export function isVideoItem(item) {
  if (!item) return false;
  return (
    item.media_type === MEDIA_TYPE_VIDEO ||
    Boolean(item.videoUrl) ||
    (typeof item.durationMs === 'number' && item.durationMs > 0)
  );
}

/** Playable source for a video item, or null if the upload hasn't landed yet. */
export function videoSource(item) {
  if (!isVideoItem(item)) return null;
  return item?.videoUrl || item?.url || null;
}

/**
 * Poster frame / thumbnail for any item. Same field order as the photo path so
 * one item type can't silently fall back differently from the other.
 */
export function posterSource(item) {
  return item?.thumbUrl || item?.thumbnailUrl || item?.fullUrl || item?.url || null;
}

/**
 * Whether there is anything to render for this item yet — a poster/thumbnail,
 * a full rendition, or (for a clip that beat its poster to Firestore) the
 * media file itself.
 *
 * Every grid filters its items through this, and the viewer dialogs are opened
 * with the index into the *filtered* list. Callers must therefore share this
 * one predicate rather than each spelling out its own field ladder: a grid that
 * kept one more item than the dialog would open the wrong photo on every click
 * after the first gap.
 */
export function hasRenderableMedia(item) {
  return Boolean(posterSource(item) || videoSource(item));
}

/**
 * Clip length as m:ss. Returns null when unknown so callers render nothing
 * rather than a misleading "0:00" on an item whose duration never made it
 * through (an older client, or a poster-only row still uploading).
 */
export function formatDuration(durationMs) {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
    return null;
  }
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Noun for this item, for alt text and captions. */
export function mediaLabel(item) {
  return isVideoItem(item) ? 'Video' : 'Image';
}

// A category id the app generated, e.g. `category_1788166507185_0`. Mobile
// writes the id into `label` as well as `category_id`, so "the first non-empty
// of the two" is not a name — it is the same id twice.
const SYNTHETIC_CATEGORY_ID = /^category_\d+(_\d+)?$/;

// One map per template object: the template cache hands out the same object
// until the template changes, so a card re-rendering gets the same map back
// rather than a fresh prop on every pass.
const categoryNamesCache = new WeakMap();

/**
 * A template's photo category names by category id, as the template spells
 * them (`image_categories[].name`, the source language; translations live
 * elsewhere on the template). Null when the template is missing or names no
 * category, which leaves `categoryLabel` on the stored name.
 */
export function templateCategoryNames(template) {
  const categories = template?.image_categories;
  if (!Array.isArray(categories) || categories.length === 0) return null;
  if (categoryNamesCache.has(template)) return categoryNamesCache.get(template);
  // No prototype, so a category id like `constructor` can't read one back.
  const names = Object.create(null);
  categories.forEach((category) => {
    const id = typeof category?.id === 'string' ? category.id.trim() : '';
    const name = typeof category?.name === 'string' ? category.name.trim() : '';
    if (id && name) names[id] = name;
  });
  const result = Object.keys(names).length > 0 ? names : null;
  categoryNamesCache.set(template, result);
  return result;
}

/**
 * `templateCategoryNames` for a task's own template, looked up with the
 * TemplateContext's `getTemplateById`. Null when the task names no template
 * or the workspace no longer has it.
 */
export function taskCategoryNames(task, getTemplateById) {
  const templateId = task?.templateId || task?.template_id;
  if (!templateId || typeof getTemplateById !== 'function') return null;
  return templateCategoryNames(getTemplateById(templateId));
}

/**
 * The photo category as a person named it.
 *
 * With `categoryNames` (see `templateCategoryNames`), the template's own name
 * for the item's `category_id` wins. `category_name` is a snapshot the phone
 * took at capture, and until #799 it was the operator's translation: a Thai
 * phone captioned an English template's photos in Thai while the field labels
 * beside them, and every export, read English. The template's name is what
 * the exports use, so the web agrees with them.
 *
 * Without it, or for a category the template no longer has: uploads carry
 * both `category_id` and `category_name`; the viewers read the id first and
 * were captioning every photo `category_1788499890317`. The name is the only
 * field here that a human chose, so it wins over the id.
 *
 * Falling back to the id would only reinstate the bug in a narrower case, so
 * an id-shaped fallback returns null and the caller renders no chip at all.
 * Legacy tasks whose `label` holds a real category name still get one.
 */
export function categoryLabel(item, categoryNames = null) {
  if (!item) return null;
  const id = item.category_id || item.categoryId;
  if (categoryNames && typeof id === 'string') {
    const fromTemplate = categoryNames[id.trim()];
    if (fromTemplate) return fromTemplate;
  }
  const named = item.category_name || item.categoryName;
  if (typeof named === 'string' && named.trim()) return named.trim();

  const fallback = item.label || item.category || item.category_id;
  if (typeof fallback !== 'string' || !fallback.trim()) return null;
  return SYNTHETIC_CATEGORY_ID.test(fallback.trim()) ? null : fallback.trim();
}

/**
 * The per-shot photo label the operator was prompted with (the category's
 * `photo_labels[n]`), or null.
 *
 * Mobile writes it into `images[].label` from 2026-09-15. Before that the same
 * field held the category id (`pallets`, `category_1788…`), and some legacy
 * rows hold the category name — none of which is a photo label, and showing it
 * would only repeat the category chip. So anything matching the item's own
 * category, or shaped like a generated id, reads as no label.
 */
export function photoLabel(item) {
  const raw = item?.label;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const label = raw.trim();
  if (SYNTHETIC_CATEGORY_ID.test(label)) return null;
  const same = (v) => typeof v === 'string' && v.trim().toLowerCase() === label.toLowerCase();
  if (same(item.category_id) || same(item.categoryId) || same(item.category)
      || same(item.category_name) || same(item.categoryName)) {
    return null;
  }
  return label;
}

/**
 * What groups a photo with the others of its category — the id, falling back
 * to the name. '' for an item with neither, which groups the uncategorised.
 */
export function categoryKey(item) {
  const id = item?.category_id || item?.categoryId;
  if (typeof id === 'string' && id.trim()) return id.trim();
  return categoryLabel(item) || '';
}
